import { useCallback, useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { releaseUpdates, useDisplayReport } from '../../app/home/displayReport'
import { IS_MOCK } from '../../app/mockMode'
import { getTimeModels } from '../../app/time/hourModels'
import { timeCursor } from '../../app/time/cursor'
import { useApp, type SavedPlace } from '../store'
import { getFrames } from '../formatters'
import type { V5Data } from './types'
import { publishedVerification } from './published-verification'

const subscribeOnline = (cb: () => void) => {
  window.addEventListener('online', cb); window.addEventListener('offline', cb)
  return () => { window.removeEventListener('online', cb); window.removeEventListener('offline', cb) }
}

export function useV5Data(place: SavedPlace): V5Data {
  const q = useDisplayReport(place)
  const report = q.data
  const units = useApp((state) => state.units)
  const offline = useSyncExternalStore(subscribeOnline, () => navigator.onLine === false, () => false)
  const [hour, setHour] = useState(() => Math.floor(Date.now() / 3_600_000))
  useEffect(() => {
    if (IS_MOCK) return
    let timer = 0
    const arm = () => { timer = window.setTimeout(() => { setHour(Math.floor(Date.now() / 3_600_000)); arm() }, 3_600_050 - Date.now() % 3_600_000) }
    arm()
    return () => window.clearTimeout(timer)
  }, [])
  const models = useMemo(() => report ? getTimeModels(report, units, IS_MOCK ? Date.parse(report.current.time) : Date.now()) : null, [report, units, hour])
  const frames = useMemo(() => report && models ? getFrames(report, models, units, IS_MOCK) : null, [report, models, units])
  useLayoutEffect(() => {
    timeCursor.setPlace(place.id)
    timeCursor.setClock(IS_MOCK && report ? () => Date.parse(report.current.time) : Date.now)
    if (models) timeCursor.setHorizon(models.start, models.end)
    timeCursor.flush()
  }, [place.id, report, models])
  const { refetch } = q
  const refresh = useCallback(() => { releaseUpdates(); void refetch() }, [refetch])
  return useMemo(() => ({
    verificationSummary: publishedVerification,
    place, name: place.isCurrent ? report?.location.name ?? 'My Location' : place.name, report, units, models,
    snapshot: report ? { id: `${place.id}:${report.generated_at}`, generatedAt: report.generated_at, expiresAt: null, componentIds: {}, source: 'report-v2' as const, report } : null,
    now: frames?.now ?? null, frames: frames?.hours ?? [], pending: !report && !q.isError, error: q.error, offline, mock: IS_MOCK, refresh,
  }), [place, report, units, models, frames, q.isError, q.error, offline, refresh])
}
